import React, { useCallback, useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { IoClose } from "react-icons/io5";

import AdminLayout from "layouts/admin";
import AuthLayout from "layouts/auth";
import RequireAuth from "components/auth/RequireAuth";
import { UserProvider } from "contexts/UserContext";

import { NotificationProvider } from "context/NotificationContext";
import ResetPassword from "views/auth/ResetPassword";
import { isAuthenticated } from "utils/auth";
import { getDefaultPortalPath } from "utils/portalPath";

const App = () => {
  const defaultPortalPath = isAuthenticated() ? getDefaultPortalPath() : "/auth/sign-in";
  const [globalToast, setGlobalToast] = useState({
    show: false,
    message: "",
    type: "error",
  });
  const [permissionModal, setPermissionModal] = useState({
    open: false,
    title: "",
    message: "",
  });

  const showGlobalToast = useCallback((message, type = "error") => {
    setGlobalToast({ show: true, message, type });
  }, []);

  const showPermissionModal = useCallback((title, message) => {
    setPermissionModal({ open: true, title, message });
  }, []);

  const closePermissionModal = useCallback(() => {
    setPermissionModal({ open: false, title: "", message: "" });
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.__showToast = showGlobalToast;
    window.__showPermissionModal = showPermissionModal;
    return () => {
      if (typeof window !== "undefined") {
        if (window.__showToast === showGlobalToast) window.__showToast = undefined;
        if (window.__showPermissionModal === showPermissionModal) window.__showPermissionModal = undefined;
      }
    };
  }, [showGlobalToast, showPermissionModal]);

  useEffect(() => {
    if (!globalToast.show) return undefined;
    const timer = window.setTimeout(() => {
      setGlobalToast({ show: false, message: "", type: "error" });
    }, 3000);
    return () => window.clearTimeout(timer);
  }, [globalToast.show]);

  return (
    <NotificationProvider>
      {permissionModal.open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-xl rounded-3xl bg-white p-6 shadow-2xl dark:bg-slate-900">
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold text-slate-900 dark:text-white">{permissionModal.title}</h2>
                <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
                  {permissionModal.message}
                </p>
              </div>
              <button
                type="button"
                onClick={closePermissionModal}
                className="rounded-full p-2 text-slate-500 transition-colors hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                <IoClose className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={closePermissionModal}
                className="rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-600"
              >
                Okay
              </button>
            </div>
          </div>
        </div>
      )}
      {globalToast.show && (
        <div className="fixed left-1/2 top-4 z-[60] w-full max-w-md -translate-x-1/2 px-4">
          <div
            className={`rounded-2xl p-3 text-center text-sm font-semibold text-white shadow-2xl backdrop-blur-md transition-all duration-200 ${
              globalToast.type === "success"
                ? "border border-emerald-400 bg-emerald-500/95"
                : "border border-red-400 bg-red-500/95"
            }`}
          >
            {globalToast.message}
          </div>
        </div>
      )}
      <UserProvider>
        <Routes>
          <Route path="auth/*" element={<AuthLayout />} />
          <Route
            path="admin/*"
            element={
              <RequireAuth>
                <AdminLayout />
              </RequireAuth>
            }
          />
          <Route
            path="staff/*"
            element={
              <RequireAuth>
                <AdminLayout />
              </RequireAuth>
            }
          />
          <Route
            path="business-man/*"
            element={
              <RequireAuth>
                <AdminLayout />
              </RequireAuth>
            }
          />
          {/* Support top-level reset links like /reset-password?token=... */}
          <Route path="reset-password" element={<ResetPassword />} />
          <Route path="/" element={<Navigate to={defaultPortalPath} replace />} />
          <Route path="*" element={<Navigate to={defaultPortalPath} replace />} />
        </Routes>
      </UserProvider>
    </NotificationProvider>
  );
};

export default App;
